import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="toast"
// Manages toast notifications with auto-dismiss and stacking
//
// Container usage (add once in layout):
//   <div data-controller="toast"
//        data-toast-target="container"
//        class="toast toast-end toast-bottom z-50">
//   </div>
//
// Show toast from JavaScript:
//   document.dispatchEvent(new CustomEvent("toast:show", {
//     detail: {
//       type: "success",  // success, error, warning, info
//       message: "Company saved!",
//       description: "Optional additional info",
//       action: { label: "View", url: "/companies/1" },
//       autoDismiss: true,  // default: true for success/info, false for error/warning
//       duration: 3000      // default: 3000ms
//     }
//   }))
//
// Show toast from server via Turbo Stream:
//   <turbo-stream action="append" target="toast-container">
//     <template>
//       <div data-controller="toast"
//            data-toast-type-value="success"
//            data-toast-auto-dismiss-value="true"
//            data-toast-duration-value="3000">
//         Company saved!
//       </div>
//     </template>
//   </turbo-stream>
export default class extends Controller {
  static targets = ["container", "announcer"]

  static values = {
    type: { type: String, default: "info" },
    autoDismiss: { type: Boolean, default: true },
    duration: { type: Number, default: 3000 }
  }

  connect() {
    // If this is the container element, listen for toast events
    if (this.hasContainerTarget || this.element.classList.contains("toast")) {
      this.boundShowToast = this.showFromEvent.bind(this)
      document.addEventListener("toast:show", this.boundShowToast)
    } else {
      // This is an individual toast element
      this.initializeToast()
    }
  }

  disconnect() {
    if (this.boundShowToast) {
      document.removeEventListener("toast:show", this.boundShowToast)
    }
    if (this.dismissTimeout) {
      clearTimeout(this.dismissTimeout)
    }
  }

  showFromEvent(event) {
    const { type, message, description, action, autoDismiss, duration } = event.detail
    this.show({ type, message, description, action, autoDismiss, duration })
  }

  show({ type = "info", message, description, action, autoDismiss, duration = 3000 }) {
    const container = this.hasContainerTarget ? this.containerTarget : this.element

    // Determine auto-dismiss based on type if not specified
    const shouldAutoDismiss = autoDismiss !== undefined
      ? autoDismiss
      : (type === "success" || type === "info")

    const toast = this.createToastElement({
      type,
      message,
      description,
      action,
      autoDismiss: shouldAutoDismiss,
      duration
    })

    // Insert at the beginning (most recent on top)
    container.prepend(toast)

    // Announce to screen readers
    this.announceToast(message)

    // Disable animation after it plays once so Turbo reconnects don't replay it
    toast.addEventListener("animationend", () => {
      toast.style.animation = "none"
    }, { once: true })

    // Schedule auto-dismiss if enabled
    if (shouldAutoDismiss) {
      toast.dataset.toastExpiresAt = Date.now() + duration
      setTimeout(() => {
        this.dismissToast(toast)
      }, duration)
    }
  }

  createToastElement({ type, message, description, action, autoDismiss, duration }) {
    const alertClass = {
      success: "alert-success",
      error: "alert-error",
      warning: "alert-warning",
      info: "alert-info"
    }[type] || "alert-info"

    const iconSvg = this.getIconSvg(type)

    const toast = document.createElement("div")
    toast.className = `alert ${alertClass} shadow-lg`
    toast.setAttribute("role", "alert")

    let html = `
      <div class="flex items-start gap-2 flex-1">
        ${iconSvg}
        <div class="flex-1">
          <span class="font-medium">${this.escapeHtml(message)}</span>
          ${description ? `<p class="text-sm opacity-80">${this.escapeHtml(description)}</p>` : ""}
        </div>
      </div>
    `

    // Add action button if provided
    if (action && action.label) {
      if (action.url) {
        html += `<a href="${this.escapeHtml(action.url)}" class="btn btn-ghost btn-sm">${this.escapeHtml(action.label)}</a>`
      } else {
        html += `<button class="btn btn-ghost btn-sm" data-action="click->toast#handleAction">${this.escapeHtml(action.label)}</button>`
      }
    }

    // Add dismiss button for non-auto-dismiss toasts or always show for errors
    if (!autoDismiss || type === "error" || type === "warning") {
      html += `<button class="btn btn-ghost btn-sm btn-square" aria-label="Dismiss" data-action="click->toast#dismiss">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>`
    }

    toast.innerHTML = html

    return toast
  }

  getIconSvg(type) {
    const icons = {
      success: `<svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
      </svg>`,
      error: `<svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>`,
      warning: `<svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>`,
      info: `<svg class="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>`
    }
    return icons[type] || icons.info
  }

  initializeToast() {
    // Disable DaisyUI's entrance animation on reconnect (e.g. Turbo navigation)
    // so the toast doesn't re-animate when the permanent container is transferred.
    if (this.element.dataset.toastInitialized) {
      this.element.style.animation = "none"
    } else {
      this.element.dataset.toastInitialized = "true"
      // Disable animation after it plays once so Turbo reconnects don't replay it
      this.element.addEventListener("animationend", () => {
        this.element.style.animation = "none"
      }, { once: true })
    }

    // For toasts added via Turbo Stream
    const shouldAutoDismiss = this.autoDismissValue &&
      (this.typeValue === "success" || this.typeValue === "info")

    if (shouldAutoDismiss) {
      // Store the expiry timestamp so reconnects use remaining time, not a fresh timer
      if (!this.element.dataset.toastExpiresAt) {
        this.element.dataset.toastExpiresAt = Date.now() + this.durationValue
      }

      const remaining = this.element.dataset.toastExpiresAt - Date.now()

      if (remaining <= 0) {
        this.dismiss()
      } else {
        this.dismissTimeout = setTimeout(() => {
          this.dismiss()
        }, remaining)
      }
    }
  }

  dismiss() {
    this.dismissToast(this.element)
  }

  dismissToast(toast) {
    // Add transition only at dismiss time so it doesn't conflict with the
    // DaisyUI @keyframes toast entry animation (which also animates opacity).
    toast.classList.add("transition-opacity", "duration-300", "opacity-0")

    // Remove after animation
    setTimeout(() => {
      toast.remove()
    }, 300)
  }

  handleAction(event) {
    // Dispatch custom event for action handling
    const toast = event.target.closest(".alert")
    document.dispatchEvent(new CustomEvent("toast:action", {
      detail: { toast },
      bubbles: true
    }))
  }

  announceToast(message) {
    if (this.hasAnnouncerTarget) {
      this.announcerTarget.textContent = message
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
